<script>
// Components
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import UserProfile from "@/router/views/users/user-profile";

// Services
import UsersDataService from "@/services/UsersDataService";

// Helpers
import Storage from "@/helpers/storage";
import UserUtils from '@/helpers/users';
import HelperUtils from '@/helpers/helper';
import {authComputed} from "@/state/helpers";
import { getRole } from '@/helpers/role';
import { sha256 } from '@/helpers/crypto';

// External Components
import Multiselect from "vue-multiselect";
import {CoolSelect} from 'vue-cool-select';
import DatePicker  from "vue2-datepicker";
import 'vue2-datepicker/index.css';
import {VueTabs, VTab} from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css';
import CoolLightBox from 'vue-cool-lightbox';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';
import countriesISO from "i18n-iso-countries";


// Libs
import isEmpty from "is-empty";

let phoneNumberSearch = "";
export default {
  page: {
    localePicker: () => import(`vue2-datepicker/locale/${this.$i18n.locale}`),
    title: "Users Search",
    meta: [
      {
        name: "description",
        content: appConfig.description
      },
    ],
  },
  components: {
    Layout,
    UserProfile,
    PageHeader,
    Multiselect,
    CoolSelect,
    DatePicker,
    VueTabs,
    VTab,
    CoolLightBox,
  },

  computed: {
    ...authComputed
  },

  methods: {
    ...UserUtils,
    ...HelperUtils,

    getCountry(user) {
      return UserUtils.getCountryName(countriesISO, user.country.toUpperCase(), this.$i18n.locale)
    },

    getRequestParams(page, pageSize, orderBy) {
      let params = {};

      if (page) {
        params["page"] = page - 1;
      }

      if (pageSize) {
        params["size"] = pageSize;
      }

      if (orderBy) {
        params["order"] = orderBy;
      }

      params['adminId'] = this.getUser.uid;
      params['adminRole'] = getRole(this.getRole);

      return params;
    },

    handlePageChange(value) {
      this.pageSelected = value;

      switch (this.currentSearch) {
        case 'all':
          this.searchFilters();
          break;

        case 'searchBy':
          this.searchBy();
          break;

        case 'searchUsersToVerify':
          this.searchUsersToVerify();
          break;
      }
    },

    handleFilterChange() {
      this.pageSelected = 1;

      switch (this.getSearchType) {
        case 'all':
          this.searchFilters();
          break;

        case 'searchBy':
          this.searchBy();
          break;

        case 'searchUsersToVerify':
          this.searchUsersToVerify();
          break;
      }
    },

    handleDateChange() {
      let now = new this.$moment();
      switch(this.filters.search.dates.selected) {
        case "always":
          this.filters.search.fromDate = null;
          this.filters.search.toDate = null;
          break;
        case "today":
          this.filters.search.fromDate = now.format('YYYY-MM-DD');
          this.filters.search.toDate = now.format('YYYY-MM-DD');
          break;
        case "yesterday":
          this.filters.search.toDate = now.format('YYYY-MM-DD');
          this.filters.search.fromDate = now.subtract(1, 'day').format('YYYY-MM-DD');
          break;
        case "curMonth":
          this.filters.search.toDate = now.format('YYYY-MM-DD');
          this.filters.search.fromDate = now.startOf('month').format('YYYY-MM-DD');
          break;
        case "lastMonth":
          this.filters.search.fromDate = now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.filters.search.toDate = now.endOf('month').format('YYYY-MM-DD');
          break;
        case "last3Month":
          this.filters.search.toDate = now.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
          this.filters.search.fromDate = now.subtract(3, 'month').startOf('day').format('YYYY-MM-DD');
          break;
        case "curYear":
          this.filters.search.toDate = now.format('YYYY-MM-DD');
          this.filters.search.fromDate = now.startOf('year').format('YYYY-MM-DD');
          break;
        case "custom":
          this.filters.search.fromDate = null;
          this.filters.search.toDate = null;
          break;
      }
    },

    getImage(userId, mediaId, elt) {
      return Storage.getUserMedia(userId, mediaId, elt);
    },

    getMediaThumb() {
      if (this.users.length > 0)
        this.users.forEach(user => {
          if (user.photo && user.photo.length > 0)
            Storage.getUserMedia(user.userId, user.photo, user.userId+'-img');
          if (user.video && user.video.length > 0)
            Storage.getUserMedia(user.userId, user.video, user.userId+'-video');
        });
    },

    searchFilters() {
      const params = this.getRequestParams(
          this.pageSelected,
          this.pageSizes.selected,
          this.orderBy.selected
      );

      if (this.filters.search.genders.selected !== null)
        params['gender'] = this.filters.search.genders.selected;

      if (this.filters.search.searchGender.selected !== null) {
        let genders = [];
        this.filters.search.searchGender.selected.forEach(gender => {
          genders.push(gender.value)
        });
        params['searchGender'] = genders;
      }

      if (this.filters.search.searchType.selected !== null) {
        let types = [];
        this.filters.search.searchType.selected.forEach(t => {
          types.push(t.value)
        });
        params['searchType'] = types;
      }

      if (this.filters.search.locality !== null)
        params['locality'] = this.filters.search.locality;

      if (this.filters.search.countries.selected !== null) {
        let countries = [];
        this.filters.search.countries.selected.forEach(country => {
          countries.push(country.alpha2)
        });
        params['countries'] = countries;
      }

      if (this.filters.search.ageMin !== null)
        params['ageMin'] = this.filters.search.ageMin;

      if (this.filters.search.ageMax !== null)
        params['ageMax'] = this.filters.search.ageMax;

      if (this.filters.search.dates.selected === 'custom' && this.filters.search.dates.dateRange !== null) {
        this.filters.search.fromDate = this.$moment(this.filters.search.dates.dateRange[0]).format("YYYY-MM-DD 00:00:00");
        this.filters.search.toDate = this.$moment(this.filters.search.dates.dateRange[1]).format("YYYY-MM-DD 23:59:59");
      }

      if (this.filters.search.toDate !== null)
        params['toDate'] = this.filters.search.toDate;

      if (this.filters.search.fromDate !== null)
        params['fromDate'] = this.filters.search.fromDate;

      if (this.filters.search.online === true)
        params['online'] = true;

      if (this.filters.search.hasPhoto === true)
        params['photo'] = true;

      if (this.filters.search.hasVideo === true)
        params['video'] = true;

      if (!isEmpty(this.filters.search.name)) {
        this.filters.search.name = this.filters.search.name.trim();
        if (this.filters.search.name.length >= 3)
          params['name'] = this.filters.search.name;
        else
        {
          return this.$toast.error(this.$t('users.search.errorNameTooShort'));
        }
      }

      if (this.$store.state.layout.timezone === 'utc') {
        params['tz'] = this.$moment.tz.guess();
      }

      this.isLoading = true;
      this.currentSearchToken = this.computeCurrentToken(params, "searchFilters");
      params["token"] = this.currentSearchToken;
      UsersDataService.getAll(params)
          .then(response => {
            const { items, totalItems, token } = response.data;
            if (token !== this.currentSearchToken) {
              return;
            }

            this.currentSearch = 'all';
            const users = [];
            if (items.length > 0) {
              for (const item of items) {
                users.push(UserUtils.normalize(item));
              }
            }

            this.users = items;
            this.count = totalItems;
            this.getMediaThumb();
            this.isLoading = false;
          })
          .catch(error => {
            window.console.error(error);
            this.isLoading = false;
            this.$toast.error(this.$t("users.search.errorNoResult"));
          })
    },

    goToUpdatesVerify() {
      this.$router.push("/users/updates");
    },

    searchUsersToVerify() {
      const params = this.getRequestParams(
          this.pageSelected,
          this.pageSizes.selected,
          this.orderBy.selected
      );

      this.isLoading = true;
      this.currentSearchToken = this.computeCurrentToken(params, "searchUsersToVerify");
      params["token"] = this.currentSearchToken;
      UsersDataService.getUsersToVerify(params)
          .then(response => {
            const { items, totalItems, token } = response.data;
            if (token !== this.currentSearchToken) {
              return;
            }
            this.currentSearch = 'searchUsersToVerify';
            const users = [];
            if (items.length > 0) {
              for (const item of items) {
                users.push(UserUtils.normalize(item));
              }
            }

            this.users = items;
            this.count = totalItems;
            this.getMediaThumb();
            this.isLoading = false;
          })
          .catch(error => {
            this.isLoading = false;
            window.console.error(error);
            this.$toast.error(error.message);
          })
    },

    formatSearchPhone(event) {
      const phoneDetails = event;
      if (event.isValid === true) {
        phoneNumberSearch = phoneDetails.formatInternational;
      }
    },

    computeCurrentToken(params, section) {
      return sha256(JSON.stringify(params) + "-" + section);
    },

    searchBy() {
      if (this.filterSearchData.trim() === '' && this.filterSearchType !== "p") {
        this.$toast.error(this.$t("users.search.byErrorValueEmpty"));
        return;
      }
      window.console.log(`searchBy this.filterSearchPhoneData: ${this.filterSearchPhoneData}`);
      if (phoneNumberSearch === '' && this.filterSearchType === "p") {
        this.$toast.error(this.$t("users.search.byErrorValueEmpty"));
        return;
      }

      const params = this.getRequestParams(
          this.pageSelected,
          this.pageSizes.selected,
          this.orderBy.selected
      );

      params["value"] = this.filterSearchType === 'p' ? phoneNumberSearch : this.filterSearchData;
      params["type"] = this.filterSearchType;

      this.isLoading = true;
      this.currentSearchToken = this.computeCurrentToken(params, "searchBy");
      params["token"] = this.currentSearchToken;
      UsersDataService.getSearchBy(params)
          .then(response => {
            const { items, totalItems, token } = response.data;
            if (token !== this.currentSearchToken) {
              return;
            }
            this.currentSearch = 'searchBy';
            const users = [];
            if (items.length > 0) {
              for (const item of items) {
                users.push(UserUtils.normalize(item));
              }
            }

            this.users = users;
            this.count = totalItems;
            this.getMediaThumb();
            this.isLoading = false;
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(error.message);
            this.isLoading = false;
          })
    },

    openUserPane() {
      if (!this.isUserPaneDisplayed) {
        this.isUserPaneDisplayed = true;
      }
    },

    closeUserPane() {
      this.userTabsActive = '';
      this.isUserPaneDisplayed = false;
    },

    showUser(user) {
      const isUserAlreadyDisplayed = this.userTabs.findIndex(x => x.userId === user.userId);
      let forceShow = false;
      if (isUserAlreadyDisplayed === -1) {
        this.userTabs.push(user);
        forceShow = true;
      }

      this.openUserPane();

      if (forceShow || (!forceShow && this.userTabsActive !== user.userId)) {
        this.userTabsActive = '';
        setTimeout(() => { this.userTabsActive = user.userId; }, 100);
      }
    },

    checkedUser(user) {
      const params = {
        adminId: this.getUser.uid
      };
      return UserUtils
          .checkedUser(user.userId, params)
          .then(() => {
            user.checked = '1';
            this.markUserVerified(user);
            this.$toast.success(this.$t('checked_success'));
          })
          .catch(error => {
            window.console.error("checkedUser", error);
            this.$toast.error(error.message);
          });
    },

    markUserVerified(user) {
      if (this.currentSearch !== "searchUsersToVerify")
        return;

      this.count--;

      const index = this.users.findIndex(item => item.userId === user.userId);
      if (index >= 0)
        this.users.splice(index, 1);
    },

    showMedia(user, type) {
      const mediaId = String(type === 0 ? user.photo : user.video.replace(".jpg", ".mp4")).replace("th_", "");
      Storage.getUserMediaUrl(user.userId, mediaId)
        .then((url) => {
          const media = {
            "id": 1,
            "userId": user.userId,
            "mediaId": mediaId,
            "createdAt":"",
            "typeMedia": type,
            "src": url,
            "title":"1/1",
          };

          this.showUserMedia([media], 0);
        });
    },

    showUserMedia(media, index) {
      this.userMedia.media = media;
      this.userMedia.index = index;
    },

    removeUserTab(userId) {
      const index = this.userTabs.findIndex(x => x.userId === userId);
      if (index > -1)
        this.userTabs.splice(index, 1);

      if (this.userTabs.length === 0)
        this.closeUserPane();
    },

    confirmDisable(user) {
      const h = this.$createElement;
      const userImg = document.getElementById(user.userId+'-img').style.backgroundImage;
      const messageVNode = h('div', {
        class:'text-center'
      }, [
        h('span', {
          class: "d-inline-block rounded-circle avatar-sm",
          style: {
            backgroundImage: userImg,
            verticalAlign: 'middle'
          }
        }),
        h('span', {
          class: "d-inline-block text-muted m-2",
          style: {
            verticalAlign: 'middle'
          },
          domProps: {
            innerHTML: this.$t("users.confirm_disable", {name: user.name})
          }
        })
      ]);

      this.$bvModal.msgBoxConfirm([messageVNode],  {
        title: this.$t('confirm_title'),
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.$t('confirm'),
        cancelTitle: this.$t('cancel'),
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      }).then(value => {
        if (value) {
          this.isProcessingAction = true;
          UserUtils
              .disableUser(user.userId, this.getUser.uid)
              .then(() => {
                this.isProcessingAction = false;
                user.disabled = 1;
              })
              .catch(error => {
                let errorMsg = error.message;
                if (errorMsg === "subscription") {
                  errorMsg = this.$t("user.active_subscription");
                }
                this.$toast.error(errorMsg);
                window.console.error(error);
                this.isProcessingAction = false;
              });
        }
      });
    },

    confirmDelete(user) {
      const h = this.$createElement;
      const userImg = document.getElementById(user.userId+'-img').style.backgroundImage;
      const messageVNode = h('div', {
          class:'text-center'
      }, [
          h('span', {
            class: "d-inline-block rounded-circle avatar-sm",
            style: {
              backgroundImage: userImg,
              verticalAlign: 'middle'
            }
          }),
          h('span', {
            class: "d-inline-block text-muted m-2",
            style: {
              verticalAlign: 'middle'
            },
            domProps: {
              innerHTML: this.$t("users.confirm_delete", {name: user.name})
            }
          })
        ]);

      this.$bvModal.msgBoxConfirm([messageVNode],  {
          title: this.$t('confirm_title'),
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: this.$t('confirm'),
          cancelTitle: this.$t('cancel'),
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }).then(value => {
          if (value) {
            this.isProcessingAction = true;
            UserUtils
                .deleteUser(user.userId, this.getUser.uid)
                .then(() => {
                  this.deleteUser(user.userId);
                  this.isProcessingAction = false;
                })
                .catch(error => {
                  let errorMsg = error.message;
                  if (errorMsg === "subscription") {
                    errorMsg = this.$t("user.active_subscription");
                  }
                  this.$toast.error(errorMsg);
                  window.console.error(error);
                  this.isProcessingAction = false;
                });
          }
      });
    },

    showUserContextMenu(event, user) {
      this.$refs.userContextMenu.showMenu(event, user)
    },

    processUserContextMenu(event) {
        const { item, option } = event;
        switch (option.value) {
          case "verify":
            if (parseInt(item.checked) === 0 && item.disabled === 0) {
              this.checkedUser(item);
            }
            break;

          case "show":
            this.showUser(item);
            break;

          case "disable":
            if (item.disabled === 0) {
              this.confirmDisable(item);
            }
            break;

          case "delete":
            this.confirmDelete(item);
            break;
        }
    },

    deleteUser(userId) {
      this.removeUserTab(userId);
      const index = this.users.findIndex(item => item.userId === userId);
      if (index >= 0)
        this.users.splice(index, 1);

    }
  },
  data() {
    return {
      isLoading: false,
      currentSearchToken: "",
      userMedia: {
        media: [],
        index: -1
      },
      optionsContext: [
        {
          name: this.$t("markAsVerify"),
          value: 'verify'
        },
        {
          name: this.$t("show_user"),
          value: 'show'
        },
        {
          type: "divider"
        },
        {
          name: this.$t("disable"),
          value: 'disable',
        },
        {
          name: this.$t("delete"),
          value: 'delete'
        },
      ],
      isProcessingAction: false,
      users: [],
      userTabs:[],
      userTabsActive: '',
      images: [],
      count: 0,
      photos:[],
      title: this.$t("menuitems.members.search"),
      pageSelected: 1,
      pageSizes: {
        options: ['20', '50', '100'],
        selected: '20'
      },
      isUserPaneDisplayed: false,
      orderBy: {
        options: [
            {value:'createdAt ASC', name: this.$t("users.search.createdAt_asc")},
            {value:'createdAt DESC', name: this.$t("users.search.createdAt_desc")},
            {value:'name ASC', name: this.$t("users.search.name_asc")},
            {value:'name DESC', name: this.$t("users.search.name_desc")},
            {value:'loggedAt ASC', name: this.$t("users.search.loggedAt_asc")},
            {value:'loggedAt DESC', name: this.$t("users.search.loggedAt_desc")}
          ],
        selected: 'createdAt DESC'
      },
      currentSearch: 'all',
      filterSearchType: 'u',
      filterSearchData: '',
      filterSearchPhoneData: '',
      filters: {
        search: {
          genders: {
            selected: null,
            options: [
              {
                value: 1,
                name: this.$t("users.search.genderMale")
              },
              {
                value: 2,
                name: this.$t("users.search.genderFemale")
              }
            ]
          },
          countries: {
            selected: null,
            options: require(`@/assets/json/countries_${this.$i18n.locale}.json`)
          },
          searchGender: {
            selected: null,
            options: [
              { value: 1, label: this.$t("user.male")},
              { value: 2, label: this.$t("user.female")}
            ],
          },
          searchType: {
            selected: null,
            options: [
              {value: 1, label: this.$t("search.type_serious")},
              {value: 2, label: this.$t("search.type_fun")},
              {value: 3, label: this.$t("search.type_undecided")}
            ],
          },
          name: null,
          locality: null,
          online: false,
          hasVideo: false,
          hasPhoto: false,
          ageMin: null,
          ageMax: null,
          fromDate: null,
          toDate: null,

          dates: {
            dateRange: null,
            selected: 'always',
            options: [
                {value:'today', text:this.$t("dates.today")},
                {value:'yesterday', text:this.$t("dates.yesterday")},
                {value:'curMonth', text:this.$t("dates.curMonth")},
                {value:'lastMonth', text:this.$t("dates.lastMonth")},
                {value:'last3Month', text:this.$t("dates.last3Month")},
                {value:'curYear', text:this.$t("dates.curYear")},
                {value:'always', text:this.$t("dates.always")},
                {value:'custom', text:this.$t("dates.custom")}
              ]
          }
        }
      }
    };
  },

  created() {
    countriesISO.registerLocale(require("i18n-iso-countries/langs/"+this.$i18n.locale+".json"));
    this.searchFilters();
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="position-relative">

      <b-button
          class="position-fixed"
          style="top: 77px; right: 15px;z-index:150"
          v-show="!isUserPaneDisplayed && userTabs.length > 0"
          @click="openUserPane"
          variant="outline-primary">
        <i class="bx bxs-cog"></i> Show Panel
      </b-button>

      <div class="card">
        <div class="card-body row">

          <div class="col-12">
            <!-- Filters Search By -->
            <h5 class="card-title mb-2">
              <b-icon icon="search" aria-hidden="true"></b-icon> {{ $t("users.search.titleFilterBy") }}
            </h5>
            <b-form inline class="row gy-2 gx-3 align-items-center mb-2" id="searchByForm" @submit.stop.prevent>
              <div class="col-sm-auto d-flex">
                <b-form-select v-model="filterSearchType" class="form-select">
                  <b-form-select-option value="u">{{ $t("users.search.byUserId") }}</b-form-select-option>
                  <b-form-select-option value="e">{{ $t("users.search.byEmail") }}</b-form-select-option>
                  <b-form-select-option value="p">{{ $t("users.search.byPhone") }}</b-form-select-option>
                </b-form-select>
              </div>
              <div class="col-sm-auto d-flex">
                <MazPhoneNumberInput
                    v-show="filterSearchType === 'p'"
                    v-model="filterSearchPhoneData"
                    @update="formatSearchPhone($event)"
                />
                <b-form-input
                    type="text"
                    v-show="filterSearchType !== 'p'"
                    :placeholder="$t('users.search.valueFilterBy')"
                    v-model="filterSearchData"
                ></b-form-input>
              </div>
              <div class="col-sm-auto d-flex">
                <b-button variant="primary" @click="searchBy">{{ $t("find_user") }}</b-button>
              </div>
            </b-form>

            <hr>
            <!-- Filters Search -->
            <h5 class="card-title">
              <b-icon icon="gear-fill" aria-hidden="true"></b-icon> {{ $t("users.search.titleFilters") }}
            </h5>
            <b-form inline class="row gy-2 gx-3 align-items-center mb-2" id="searchFiltersForm" @submit.stop.prevent>
              <!-- dates -->
              <div class="col-sm-auto d-flex">
                <b-form-select
                    id="searchDatesFilter"
                    @change="handleDateChange"
                    v-model="filters.search.dates.selected"
                    class="form-select me-lg-1"
                    :options="filters.search.dates.options">
                </b-form-select>
                <date-picker
                    v-show="filters.search.dates.selected === 'custom'"
                    :lang="$i18n.locale"
                    :placeholder="this.$t('choose_date_interval')"
                    type="date"
                    value-type="format"
                    show-week-number
                    v-model="filters.search.dates.dateRange"
                    class="me-lg-2"
                    range append-to-body ></date-picker>
              </div>
              <!-- gender -->
              <div class="col-sm-auto">
                <cool-select
                    v-model="filters.search.genders.selected"
                    :items="filters.search.genders.options"
                    :placeholder="filters.search.genders.selected ? '' : $t('users.search.gender')"
                    item-value="value"
                    item-text="name"
                >
                  <template slot="item" slot-scope="{ item: gender }">
                    <div style="display: flex; align-items: center">
                      <img :src="getGenderImg(gender.value)" class="gender-img" />
                      <div class="offset-0">{{ gender.name }}</div>
                    </div>
                  </template>
                  <!-- slot for the selected item (in the text field) -->
                  <template slot="selection" slot-scope="{ item: gender }">
                    <div style="display: flex; align-items: center">
                      <img :src="getGenderImg(gender.value)" class="gender-img" />
                      <div class="offset-0">{{ gender.name }}</div>
                    </div>
                  </template>
                </cool-select>
              </div>
              <!-- searchGender -->
              <div class="col-sm-auto">
                <multiselect
                    v-model="filters.search.searchGender.selected"
                    :multiple="true"
                    label="label"
                    track-by="value"
                    :close-on-select="false"
                    :placeholder="$t('users.search.searchGender')"
                    class="pe-2"
                    :options="filters.search.searchGender.options">
                  <template slot="option" slot-scope="props">
                    <img :src="getGenderImg(props.option.value)" class="gender-img" />
                    <div class="d-inline">{{ props.option.label }}</div>
                  </template>
                </multiselect>
              </div>
              <!-- searchType -->
              <div class="col-sm-auto">
                <multiselect
                    v-model="filters.search.searchType.selected"
                    :multiple="true"
                    label="label"
                    track-by="value"
                    :close-on-select="false"
                    class="pe-2"
                    :placeholder="$t('users.search.searchType')"
                    :options="filters.search.searchType.options">
                  <template slot="option" slot-scope="props">
                    <div class="d-inline">{{ props.option.label }}</div>
                  </template>
                </multiselect>
              </div>
              <!-- countries -->
              <div class="col-sm-auto">
                <multiselect
                    v-model="filters.search.countries.selected"
                    :multiple="true"
                    label="name"
                    track-by="name"
                    :close-on-select="false"
                    class="pe-2"
                    :placeholder="$t('users.search.country')"
                    :options="filters.search.countries.options">
                  <template slot="option" slot-scope="props">
                    <country-flag :rounded="true" :country='props.option.alpha2' />
                    <div class="d-inline">{{ props.option.name }}</div>
                  </template>
                </multiselect>
              </div>
              <!-- locality -->
              <div class="col-sm-auto">
                <b-form-input class="form-control" id="locality" :placeholder="$t('locality')" v-model="filters.search.locality"></b-form-input>
              </div>
              <!-- name -->
              <div class="col-sm-auto">
                <b-form-input class="form-control" id="name" :placeholder="$t('name')" v-model="filters.search.name"></b-form-input>
              </div>
              <!-- age min -->
              <div class="col-sm-auto">
                <b-form-input id="ageMin" :placeholder="$t('users.search.ageMin')" v-model="filters.search.ageMin" trim></b-form-input>
              </div>
              <!-- age max -->
              <div class="col-sm-auto">
                <b-form-input id="ageMax" :placeholder="$t('users.search.ageMax')" v-model="filters.search.ageMax" trim></b-form-input>
              </div>
              <!-- online -->
              <div class="col-sm-auto">
                <b-form-checkbox
                    v-model="filters.search.online"
                    id="filter-online"
                    class="form-check"
                >{{ $t("users.search.isOnline") }}</b-form-checkbox>
              </div>
              <!-- hasPhoto -->
              <div class="col-sm-auto">
                <b-form-checkbox
                    v-model="filters.search.hasPhoto"
                    id="filter-hasphoto"
                    class="form-check"
                >{{ $t("users.search.hasPhoto") }}</b-form-checkbox>
              </div>
              <!-- hasVideo -->
              <div class="col-sm-auto">
                <b-form-checkbox
                    v-model="filters.search.hasVideo"
                    id="filter-hasvideo"
                    class="form-check"
                >{{ $t("users.search.hasVideo") }}</b-form-checkbox>
              </div>
            </b-form>
            <b-button variant="primary" @click="searchFilters">{{ $t("find_users") }}</b-button>

            <hr>
            <!-- Filters Search Verify -->
            <h5 class="card-title mb-2">
              <b-icon icon="lock" aria-hidden="true"></b-icon> Vérifications
            </h5>
            <b-button class="mt-2 me-3"  variant="warning" @click="searchUsersToVerify" >{{ $t("users.search.verifyBtn") }}</b-button>
            <b-button class="mt-2"  variant="outline-primary" @click="goToUpdatesVerify">{{ $t("users.search.verifyUpdateBtn") }}</b-button>
          </div>

        </div>
      </div>

      <!-- Loader -->
      <div class="row d-flex justify-content-center">
        <vue-ellipse-progress
            v-show="isLoading"
            :loading="isLoading"
            :angle="-90"
            color="#50a5f1"
            emptyColor="#999"
            emptyColorFill="#f8f8fb"
            :size="100"
            :thickness="3"
            :emptyThickness="2"
            lineMode="out 2"
            animation="bounce 700 400"
            fontColor="#666"
            :half="false"
            class="mt-5 mb-5"
            fontSize="1rem">
        </vue-ellipse-progress>
      </div>

      <!-- Top list tools -->
      <div class="row mb-3">
        <div class="col-lg-auto col-12">
          <!-- Display settings -->
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleFilterChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>
          <!-- Order settings -->
          <b-select class="form-select d-inline w-auto m-1" v-model="orderBy.selected" @change="handleFilterChange">
            <option v-for="order in orderBy.options" :key="order.name" :value="order.value">{{ $t('orderBy') }} {{ order.name }}</option>
          </b-select>

          {{ count }} {{ $t('resultsCount') }}
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="pageSelected"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="users-list"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Result list -->
      <div class="row bg-white" v-if="!isLoading && users.length > 0">
        <div class="table-responsive">
          <table
              id="users-list"
              class="table project-list-table table-nowrap table-centered table-borderless align-middle">
            <thead>
            <tr>
              <th scope="col" style="width:130px !important;"></th>
              <th scope="col">{{ $t("users.user") }}</th>
              <th scope="col">QRIndex</th>
              <th scope="col">{{ $t("status") }}</th>
              <th scope="col">{{ $t("vip") }}</th>
              <th scope="col">{{ $t("gender") }} / {{ $t("age") }}</th>
              <th scope="col">{{ $t("email") }} / ID</th>
              <th scope="col">{{ $t("date") }}</th>
              <th scope="col">{{ $t("ip") }}</th>
              <th scope="col">{{ $t("action") }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="user in users" :key="user.userId"
                @contextmenu.prevent.stop="showUserContextMenu($event, user)"
                :class="{
                'bg-light': user.disabled === 1
                }">
              <td>
                <a href="javascript: void(0);" @click="showMedia(user, 0)" v-show="user.photo && user.photo.length > 0" class="d-inline-block">
                  <div :id="user.userId + '-img'" class="rounded-circle avatar-sm"  />
                </a>
                <span class="badge rounded-pill m-n3 bg-danger" v-show="user.countMedia>0">{{ user.countMedia }}</span>
                <a href="javascript: void(0);" @click="showMedia(user, 1)" v-show="user.video && user.video.length > 0" class="d-inline-block ms-4">
                  <div :id="user.userId + '-video'" class="rounded-circle avatar-sm" />
                </a>
                <span class="badge rounded-pill m-n3 bg-primary" v-show="user.video && user.video.length > 0"><b-icon icon="play-fill" aria-hidden="true" ></b-icon></span>
              </td>
              <td>
                <b>{{ user.name }}</b>
                <span
                    class="badge ms-1"
                    :class="{
                      'bg-success': `${user.status}` === 'online',
                      'bg-dark': `${user.status}` === 'off',
                    }"
                >{{ user.status }}</span>
                <br>
                <country-flag :country='user.country' size='small'/> {{ user.locality }}, {{ getCountry(user) }}
                <br><small>{{ user.ip || '-' }}</small>
              </td>
              <td>
                <b>{{ user.qrIndex }}</b><br><small>( {{ user.test_qrIndex2 || "-" }})</small>
              </td>
              <td>
                <span class="badge bg-dark" v-show="user.disabled === 1">{{ $t("user_disabled") }}</span>
                <span class="badge bg-warning" v-show="user.isRegistered === 0">{{ $t("incomplete") }}</span>
                <span class="badge bg-info" v-show="user.isRegistered === 1 && user.disabled === 0">{{ $t("complete") }}</span>
                <br>{{ user.slGroup }}
              </td>
              <td>
                <img :src="getSubscribeImg(user.isSubscribed)" />
              </td>
              <td>
                <img :src="getGenderImg(user.sex)" alt=""> /
                <span class="lead">{{ getAge(user.birth) }}</span> /
                <img :src="getGenderSearchImg(user.search)" alt="">
              </td>
              <td> {{ user.email }} <br>
                <small>{{ user.userId }}</small><br>
                <small>{{ user.phone }}</small><br>
              </td>
              <td>
                <i class="bx bx-calendar-event"></i> <b>{{ new Date(user.createdAt) | moment('DD MMM YYYY HH:mm') }}</b><br>
                <i class="mdi mdi-connection"></i> {{ new Date(user.loggedAt) | moment('DD MMM YYYY HH:mm') }}
                <span class="font-size-11 text-info" v-if="parseInt(user.checked) === 1"><br><i class="bx bx-check"></i> {{ new Date(user.checkedAt) | moment('DD MMM YYYY HH:mm') }} ({{ getInitials(user.checkedBy) }})</span>
              </td>
              <td>
                <i class="fas fa-network-wired"></i> {{ user.registerIp || '-' }}
              </td>
              <td>
                  <vue-ellipse-progress
                      v-show="isProcessingAction"
                      :loading="isProcessingAction"
                      :angle="-90"
                      color="#50a5f1"
                      emptyColor="#999"
                      emptyColorFill="#f8f8fb"
                      :size="30"
                      :thickness="3"
                      :emptyThickness="2"
                      lineMode="out 2"
                      animation="bounce 700 400"
                      fontColor="#666"
                      :half="false"
                      class="mt-5 mb-5"
                      fontSize="1rem">
                  </vue-ellipse-progress>
                <b-dropdown
                    class="card-drop"
                    variant="white"
                    menu-class="dropdown-menu-end"
                    right
                    toggle-class="p-0"
                >
                  <template v-slot:button-content>
                    <i class="mdi mdi-dots-horizontal font-size-18"></i>
                  </template>
                  <b-dropdown-item v-show="parseInt(user.checked) === 0"
                      href="javascript: void(0);"
                      @click="checkedUser(user)"
                  >{{ $t("markAsVerify") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="showUser(user)"
                  >{{ $t("show_user") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="confirmDisable(user)"
                  >{{ $t("disable") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="confirmDelete(user)"
                  >{{ $t("delete") }}</b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Result Empty -->
      <div class="row" v-if="!isLoading && users.length === 0">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Empty Result</h4>
              <p>No Users were found for these filters. Try again.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- bottom list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleFilterChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="pageSelected"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="users-list"
            ></b-pagination>
          </div>
        </div>
      </div>

    </div>
    <b-sidebar
        id="sidebar-right" right
        v-model="isUserPaneDisplayed"
      >
      <vue-tabs
          v-model="userTabsActive"
          active-tab-color="#213213"
          active-text-color="white">
        <v-tab v-for="userTab in userTabs" :key="userTab.userId" :title="userTab.userId" >
          <div slot="title">{{ userTab.name }} <i @click.stop="removeUserTab(userTab.userId)" class="bx bx-x-circle" ></i></div>
          <UserProfile
              :userId="null"
              :user="userTab"
              v-on:DELETE_USER="deleteUser"
              v-on:SHOW_MEDIA="showUserMedia"
              v-on:USER_VERIFIED="markUserVerified">
          </UserProfile>
        </v-tab>
      </vue-tabs>
    </b-sidebar>

    <vue-simple-context-menu
        element-id="userContextMenu"
        :options="optionsContext"
        ref="userContextMenu"
        @option-clicked="processUserContextMenu"
    />

    <CoolLightBox
        :items="userMedia.media"
        :index="userMedia.index"
        :effect="'fade'"
        @close="userMedia.index = null">
    </CoolLightBox>
  </Layout>
</template>

<style>
  .cursor-pointer {
    cursor:pointer;
  }

  .avatar-sm {
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
  }

  .gender-img {
    max-width: 30px;
    margin-right: 10px;
  }

  .b-sidebar-right {
    width:35%;
  }

  .b-sidebar-body {
    padding:16px;
  }

  /*
  .splitpanes {
    background: none;
  }

  .splitpanes__pane {
    padding:0 20px;
    background:none;
  }

  .splitpanes__pane:first-child {
    padding-left:0;
    padding-right: 20px;
  }

  .splitpanes__pane:last-child{
    padding-left: 20px;
    padding-right:0;
  }

  .splitpanes--vertical>.splitpanes__splitter {
    width: 7px;
    border-left: 5px solid #fff;
    margin-left: -1px;
  }
*/

</style>
